<template>
  <section>
    <TableFilterView
      :columns="columns"
      :initial-filters="activeFilters"
      page="structureList"
      :show-export="true"
      :can-export-csv="false"
      :can-export-xls-all="false"
      @update-active-filters="updateActiveFilters"
      @update-visible-columns="updateVisibleColumns"
      @export-xls="exportStructuresXls"
    />
    <b-table
      :ref="tableRef"
      :striped="config.isStriped"
      :hoverable="config.isHoverable"
      :debounce-search="config.searchDebounce"
      :aria-next-label="config.nextLabel()"
      :aria-previous-label="config.previousLabel()"
      :aria-page-label="config.pageLabel()"
      :aria-current-label="config.currentPageLabel()"
      :per-page="config.perPage"
      :sticky-header="config.stickyHeader"
      :current-page="currentPagedSortedParameter.pageNumber"
      :pagination-position="config.paginationPosition"
      paginated
      backend-pagination
      :sort-icon="config.sortIcon"
      :sort-icon-size="config.sortIconSize"
      backend-sorting
      sort-multiple
      :sort-multiple-data="buefySortingPriority"
      :total="total"
      :data="structuresList"
      :loading="isLoading"
      @sort="onSort"
      @sorting-priority-removed="sortingPriorityRemoved"
      @page-change="onPageChange"
    >
      <b-table-column
        v-for="column in columns"
        :key="column.field"
        v-bind="column"
        :visible="column.visible"
      >
        <template #default="props">
          <div v-if="column.field === 'type'">
            <div>
              {{
                $t(
                  `StructureSelectionView.types-label-${props.row["type"]
                    .toLowerCase()
                    .replace(/_/g, "-")}`,
                )
              }}
            </div>
          </div>
          <div v-if="column.field === 'codeDepartement'">
            <div>{{ buildCodeDepartement(props.row["codePostal"]) }}</div>
          </div>
          <div v-if="column.field === 'grae.region'">
            <span v-if="props.row['grae']">{{ props.row["grae"].region }}</span>
          </div>
          <div
            v-if="!column.custom"
            class="clickable"
            @click="showStructureListesProjetsPage($event, props.row['id'])"
          >
            <div>{{ props.row[column.field] }}</div>
          </div>
        </template>
      </b-table-column>
    </b-table>
  </section>
</template>

<script lang="ts">
import { EnumHelper } from "@/model/bean/EnumHelper"
import { FilterDTO, PaginationOrder, StructureDTO } from "@/model/bean/GeneratedDTOs"
import { GraeService } from "@/services/rest/grae/GraeService"
import { OrganismeService } from "@/services/rest/organisme/OrganismeService"
import { defineComponent } from "vue"
import Column from "@/views/tables/Column"
import TableFilterView from "@/views/tables/TableFilterView.vue"
import { useTableView } from "@/hooks/useTableView"
import { SortOrder } from "@/model/SortOrder"
import { BTable, BTableColumn } from "buefy"

const HISTORY_PAGINATION_GRAE_STRUCTURE = "history.pagination.grae.structure"
export const URL_PREFIX_GRAE_STRUCTURE = "/structure/"

export default defineComponent({
  name: "GraeStructuresView",
  components: {
    TableFilterView,
    BTable,
    BTableColumn,
  },
  props: {
    graeId: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      graeService: GraeService.INSTANCE,
      organismeService: OrganismeService.INSTANCE,

      structuresList: [] as Array<StructureDTO>,
      graeNamesList: [] as Array<string>,

      // Utilisation du hook useTableView - sera initialisé dans created
      tableView: null as any,
    }
  },
  computed: {
    // Propriétés réactives exposées par le hook
    tableRef(): string {
      return this.tableView?.state.tableRef || "grae-structures-table"
    },
    isLoading(): boolean {
      return this.tableView?.state.isLoading || false
    },
    columns(): Array<Column> {
      return this.tableView?.state.columns || []
    },
    currentPagedSortedParameter(): any {
      return this.tableView?.state.currentPagedSortedParameter
    },
    buefySortingPriority(): Array<any> {
      return this.tableView?.state.buefySortingPriority || []
    },
    total(): number {
      return this.tableView?.state.total || 0
    },
    config(): any {
      return this.tableView?.state.config
    },
    perPage(): number {
      return this.tableView?.state.perPage || 25
    },
    activeFilters(): Array<any> {
      return this.tableView?.state.activeFilters || []
    },
  },
  created() {
    // Initialisation du tableView
    this.tableView = useTableView({
      tableRef: "grae-structures-table",
      router: this.$router,
      route: this.$route,
      i18n: this.$i18n,
      getInitialColumns: this.getInitialColumns.bind(this),
      getDefaultSort: this.getDefaultSort.bind(this),
      getInitialActiveFilters: this.getInitialActiveFilters.bind(this),
      reloadData: this.reloadData.bind(this),
      reloadDataWhenMounted: true,
      updateLocalStorageHistoryPagination: this.updateLocalStorageHistoryPagination.bind(this),
    })

    // Logique métier d'initialisation
    if (!this.graeId) {
      this.organismeService.getAllGraes().then((graes) => {
        this.graeNamesList = graes.map((g) => g.region)
        this.refreshInitialColumns()
      })
    }
  },
  mounted() {
    this.tableView?.initMounted()

    this.setPreviousPagination(
      ["/ae/", URL_PREFIX_GRAE_STRUCTURE],
      HISTORY_PAGINATION_GRAE_STRUCTURE,
    )
  },
  methods: {
    // Réexposer les méthodes du tableau
    updateActiveFilters(newActiveFilters: Array<FilterDTO>): void {
      this.tableView.updateActiveFilters(newActiveFilters)
    },

    updateVisibleColumns(newVisibleColumnList: Array<Column>): void {
      this.tableView.updateVisibleColumns(newVisibleColumnList)
    },

    onPageChange(newPage: string | number): void {
      const pageNum = typeof newPage === "string" ? parseInt(newPage) : newPage
      this.tableView.onPageChange(pageNum)
    },

    onSort(field: string | undefined, order: SortOrder): void {
      if (field) {
        this.tableView.onSort(field, order)
      }
    },

    sortingPriorityRemoved(field: string | undefined): void {
      if (field) {
        this.tableView.sortingPriorityRemoved(field)
      }
    },

    setPreviousPagination(activePrefixUrls: string[], historyPaginationStorageKey: string): void {
      this.tableView.setPreviousPagination(activePrefixUrls, historyPaginationStorageKey)
    },

    setLocalStorageHistoryPagination(historyPaginationStorageKey: string): void {
      this.tableView.setLocalStorageHistoryPagination(historyPaginationStorageKey)
    },

    refreshInitialColumns(): void {
      this.tableView.refreshInitialColumns()
    },

    mounted(): void {
      this.setPreviousPagination(
        ["/ae/", URL_PREFIX_GRAE_STRUCTURE],
        HISTORY_PAGINATION_GRAE_STRUCTURE,
      )
      this.tableView.initMounted()
    },

    updateLocalStorageHistoryPagination(): void {
      this.setLocalStorageHistoryPagination(HISTORY_PAGINATION_GRAE_STRUCTURE)
    },

    updateTotal(total: number): void {
      this.tableView.updateTotal(total)
    },

    async reloadData(): Promise<void> {
      try {
        const paginatedStructuresList = await this.graeService.getStructures(
          this.graeId,
          this.currentPagedSortedParameter,
          this.activeFilters,
        )
        this.structuresList = paginatedStructuresList.elements
        this.updateTotal(paginatedStructuresList.count)

        // Get region lists for filtering purposes
        if (!this.graeId) {
          this.organismeService.getAllGraes().then((graes) => {
            this.graeNamesList = graes.map((g) => g.region)
            this.refreshInitialColumns()
          })
        }
      } catch (e: any) {
        // Silent catch
      }
    },

    getInitialColumns(): Array<Column> {
      const initialColumns: Array<Column> = [
        {
          field: "codeDepartement",
          label: this.$t("graePage.details.structures.departement").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "nom",
          label: this.$t("graePage.details.structures.nom").toString(),
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "ville",
          label: this.$t("graePage.details.structures.ville").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "type",
          label: this.$t("graePage.details.structures.type").toString(),
          sortable: true,
          backEndSearchable: true,
          possibleSearchValues: EnumHelper.allTypesStructure,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "nbProjets",
          label: this.$t("graePage.details.structures.nbProjets").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
      ]
      if (!this.graeId) {
        initialColumns.push({
          field: "grae.region",
          label: this.$t("graeList.region").toString(),
          possibleSearchValues: this.graeNamesList,
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        })
      }
      return initialColumns
    },

    getInitialActiveFilters(): Array<FilterDTO> {
      return new Array<FilterDTO>()
    },

    getDefaultSort(): Array<PaginationOrder> {
      const defaultSort = new PaginationOrder()
      defaultSort.clause = "nom"
      defaultSort.desc = true
      return [defaultSort]
    },

    showStructureListesProjetsPage(event: Event, id: number): void {
      // Do not foward click event to row (would trigger modal)
      event.stopPropagation()
      this.$router.push(URL_PREFIX_GRAE_STRUCTURE + id + "/projets")
    },

    buildCodeDepartement(codePostal: string): string {
      return codePostal.startsWith("97") || codePostal.startsWith("98")
        ? codePostal.substring(0, 3)
        : codePostal.substring(0, 2)
    },

    exportStructuresXls(): void {
      this.organismeService.exportStructureXls(this.graeId, this.activeFilters)
    },
  },
})
</script>
